import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import moment from 'moment';

import Card from 'components/Layout/Card';
import Modal from 'components/Layout/Modal';
import UserProfileDetailsCard from 'components/Layout/UserProfileDetailsCard';

import { fetchCertificateLogDetails } from 'store/slices';
import {
  generatePreSignedUrl,
  getDataFromLocalStorage,
  paymentColor,
} from 'utils/helpers';
import { newUserCertificate } from 'utils/constants';
import Loader from 'components/Layout/Loader';
import DetailItem from 'components/Layout/DetailItem';

const EventCertificatesUserTrackingDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { eventID, logID } = useParams();
  const [searchParams] = useSearchParams();

  const type = searchParams.get('type');
  const requestId = searchParams.get('request_id');
  const userType = searchParams.get('user_type');
  const userTypeCategory = searchParams.get('user_type_category');

  const [viewSample, setViewSample] = useState('');
  const [logDetails, setLogDetails] = useState([]);
  const [certificateUrl, setCertificateUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const getCertificateLogDetails = async () => {
    const payload = {
      id: logID,
      event_id: eventID,
      type,
      request_id: requestId,
      user_type: userType,
      user_type_category: userTypeCategory,
    };

    const response = await dispatch(fetchCertificateLogDetails(payload));
    if (response.status === 200) {
      setLogDetails(response?.data);
    }
    setIsLoading(false);
  };

  const fetchCertificateUrl = async (certificate_file) => {
    if (!certificate_file) return '';
    const response = await generatePreSignedUrl(
      certificate_file,
      newUserCertificate
    );
    return response;
  };

  useEffect(() => {
    if (['0', '1', '6', '7'].includes(getDataFromLocalStorage('user_type'))) {
      getCertificateLogDetails();
    } else {
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logID]);

  const {
    abstract_details,
    user_details,
    added_by,
    admin_data,
    sadmin_data,
    certificate_log,
  } = logDetails;

  // Fetch certificate URL once certificate_log is available
  useEffect(() => {
    const getUrl = async () => {
      if (certificate_log?.certificate_file) {
        const url = await fetchCertificateUrl(
          certificate_log?.certificate_file
        );
        setCertificateUrl(url);
      }
    };
    getUrl();
  }, [certificate_log]);

  const section = {
    paperTitle: abstract_details?.paper_title,
    certificateDetails: [
      {
        label: 'Certificate Type',
        value:
          certificate_log?.certificate_type.charAt(0).toUpperCase() +
            certificate_log?.certificate_type.slice(1) || '-',
      },
      {
        label: 'Certificate Number',
        value: certificate_log?.certificate_no || '-',
      },
      {
        label: 'Certificate Status',
        value:
          certificate_log?.certificate_status !== '' ? (
            <span className={paymentColor(certificate_log?.certificate_status)}>
              {certificate_log?.certificate_status}
            </span>
          ) : (
            '-'
          ),
      },
      {
        label: 'Expire Date',
        value:
          certificate_log?.expired_at !== ''
            ? moment(certificate_log?.expired_at).format('DD-MMM-YYYY')
            : '-',
      },
      {
        label: 'Download Date',
        value:
          certificate_log?.downloaded_at !== ''
            ? moment(certificate_log?.downloaded_at).format('DD-MMM-YYYY')
            : '-',
      },
      {
        label: 'Certificate',
        value: certificateUrl || '-',
      },
    ],
    operationActivity: [
      { label: 'Added By', value: added_by?.user_name || '-' },
      { label: 'Id', value: added_by?.member_id || '-' },
      { label: 'Email Id', value: added_by?.email_id || '-' },
      {
        label: 'Uploaded',
        value: added_by?.uploaded_at
          ? moment(added_by?.uploaded_at).format('DD-MMM-YYYY')
          : '-',
      },
    ],
    adminActivity: [
      { label: 'Admin', value: admin_data?.user_name || '-' },
      { label: 'Id', value: admin_data?.member_id || '-' },
      { label: 'Email Id', value: admin_data?.email_id || '-' },
      {
        label: 'Approval',
        value: admin_data?.uploaded_at
          ? moment(admin_data?.uploaded_at).format('DD-MMM-YYYY')
          : '-',
      },
    ],
    superAdminActivity: [
      { label: 'Super-Admin', value: sadmin_data?.user_name || '-' },
      { label: 'Id', value: sadmin_data?.member_id || '-' },
      { label: 'Email Id', value: sadmin_data?.email_id || '-' },
      {
        label: 'Approval',
        value: sadmin_data?.uploaded_at
          ? moment(sadmin_data?.uploaded_at).format('DD-MMM-YYYY')
          : '-',
      },
    ],
  };

  return (
    <>
      {isLoading ? (
        <div className="d-flex align-items-center justify-content-center h-100">
          <span className="ms-3">
            <Loader size="md" />
          </span>
        </div>
      ) : (
        <>
          <div className="row">
            {/* Left Profile Card */}
          <div className="col-12 col-sm-12 col-md-5 col-lg-4">
              <UserProfileDetailsCard user_details={user_details} navigation={true}/>
            </div>

            {/* Right Detail Section */}
          <div className='col-12 col-sm-12 col-md-7 col-lg-8'>
              <Card className="cp-24 cmb-17">
                <div className="mb-2">
                  <div className="text-14-500-18 color-text-blue cmb-18">
                    Track Client Details :
                  </div>

                  {section?.paperTitle && (
                    <div className="mb-3">
                      <DetailItem
                        label="Paper Title"
                        value={section.paperTitle}
                      />
                    </div>
                  )}

                  <div className="row">
                    {section?.certificateDetails.map((item, i) => {
                      if (item.label === 'Certificate') {
                        return (
                          <div key={i} className="col-md-4 col-sm-6 col-12">
                            <DetailItem
                              label={item.label}
                              value={
                                item.value && item.value !== '-' ? (
                                  <span
                                    className="text-primary d-flex align-items-center gap-1 cursor-pointer"
                                    onClick={() => setViewSample(item.value)}
                                  >
                                    <i className="bi bi-eye" /> Preview
                                    Certificate
                                  </span>
                                ) : (
                                  '-'
                                )
                              }
                            />
                          </div>
                        );
                      }

                      return (
                        <div key={i} className="col-md-4 col-sm-6 col-12">
                          <DetailItem label={item.label} value={item.value} />
                        </div>
                      );
                    })}
                  </div>

                  {/* Operation Resource Activities */}
                  {added_by && (
                    <>
                      <div className="text-14-500-18 color-new-car cmt-20 cmb-18">
                        Resource Activities :
                      </div>
                      <div className="row">
                        {section.operationActivity.map((item, i) => (
                          <div key={i} className="col-md-3 col-sm-6 col-12">
                            <DetailItem label={item.label} value={item.value} />
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Admin Activities */}
                  {admin_data && (
                    <>
                      <div className="text-14-500-18 color-new-car cmt-20 cmb-18">
                        Admin Activities :
                      </div>
                      <div className="row">
                        {section.adminActivity.map((item, i) => (
                          <div key={i} className="col-md-3 col-sm-6 col-12">
                            <DetailItem label={item.label} value={item.value} />
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Super Admin Activities */}
                  {sadmin_data && (
                    <>
                      <div className="text-14-500-18 color-new-car cmt-20 cmb-18">
                        Super Admin Activities :
                      </div>
                      <div className="row">
                        {section.superAdminActivity.map((item, i) => (
                          <div key={i} className="col-md-3 col-sm-6 col-12">
                            <DetailItem label={item.label} value={item.value} />
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </Card>
            </div>
          </div>

          {/* Certificate Preview Modal */}
          {viewSample && (
            <Modal
              size="xl"
              onHide={() => setViewSample('')}
              titleBesideClose="Preview Certificate"
              isTitleBorder
              isTitleVerticalCenter
            >
              <div
                style={{
                  height: '80vh',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <iframe
                  frameBorder="0"
                  src={`${viewSample}#toolbar=0&navpanes=0`}
                  title="Preview Certificate"
                  style={{ width: '100%', flexGrow: 1, border: 'none' }}
                />
                <div
                  className="d-flex align-items-center justify-content-center gap-2 text-20-400-25 color-new-car pointer cp-10"
                  style={{ flexShrink: 0, padding: '12px 0' }}
                >
                  <i className="bi bi-download" /> Download Certificate
                </div>
              </div>
            </Modal>
          )}
        </>
      )}
    </>
  );
};

export default EventCertificatesUserTrackingDetails;
